import type { BoardRecord } from "../types";

const DATABASE_NAME = "funnelboard-local";
const DATABASE_VERSION = 1;
const BOARDS_STORE = "boards";
const META_STORE = "meta";
const FALLBACK_BOARDS_KEY = "funnelboard:boards:v1";
const FALLBACK_ACTIVE_KEY = "funnelboard:active-board:v1";

export class StorageUnavailableError extends Error {
  constructor(message = "Storan pelayar tidak tersedia.") {
    super(message);
    this.name = "StorageUnavailableError";
  }
}

interface StoredMeta {
  key: string;
  value: string;
}

export class BoardStorage {
  private readonly indexedDBApi?: IDBFactory;
  private readonly fallbackStorage?: Storage;
  private dbPromise?: Promise<IDBDatabase>;
  public isUsingFallback = false;

  constructor(
    indexedDBApi: IDBFactory | null | undefined = globalThis.indexedDB,
    fallbackStorage: Storage | null | undefined = globalThis.localStorage,
  ) {
    this.indexedDBApi = indexedDBApi ?? undefined;
    this.fallbackStorage = fallbackStorage ?? undefined;
  }

  private openDatabase(): Promise<IDBDatabase> {
    if (!this.indexedDBApi) {
      return Promise.reject(new StorageUnavailableError("IndexedDB tidak tersedia."));
    }

    if (!this.dbPromise) {
      this.dbPromise = new Promise((resolve, reject) => {
        const request = this.indexedDBApi!.open(DATABASE_NAME, DATABASE_VERSION);

        request.onupgradeneeded = () => {
          const db = request.result;
          if (!db.objectStoreNames.contains(BOARDS_STORE)) {
            db.createObjectStore(BOARDS_STORE, { keyPath: "id" });
          }
          if (!db.objectStoreNames.contains(META_STORE)) {
            db.createObjectStore(META_STORE, { keyPath: "key" });
          }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new StorageUnavailableError());
        request.onblocked = () => reject(new StorageUnavailableError("Pangkalan data disekat oleh tab lain."));
      });
    }

    return this.dbPromise;
  }

  private async withStore<T>(
    storeName: typeof BOARDS_STORE | typeof META_STORE,
    mode: IDBTransactionMode,
    operation: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> {
    const db = await this.openDatabase();

    return new Promise<T>((resolve, reject) => {
      const transaction = db.transaction(storeName, mode);
      const request = operation(transaction.objectStore(storeName));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new StorageUnavailableError());
      transaction.onabort = () => reject(transaction.error ?? new StorageUnavailableError());
    });
  }

  private getFallbackBoards(): BoardRecord[] {
    if (!this.fallbackStorage) {
      throw new StorageUnavailableError();
    }

    const raw = this.fallbackStorage.getItem(FALLBACK_BOARDS_KEY);
    if (!raw) return [];

    try {
      const parsed = JSON.parse(raw) as unknown;
      return Array.isArray(parsed) ? (parsed as BoardRecord[]) : [];
    } catch {
      return [];
    }
  }

  private writeFallbackBoards(boards: BoardRecord[]): void {
    if (!this.fallbackStorage) {
      throw new StorageUnavailableError();
    }
    this.fallbackStorage.setItem(FALLBACK_BOARDS_KEY, JSON.stringify(boards));
  }

  private async runWithFallback<T>(primary: () => Promise<T>, fallback: () => T): Promise<T> {
    try {
      return await primary();
    } catch {
      if (!this.fallbackStorage) throw new StorageUnavailableError();
      this.isUsingFallback = true;
      return fallback();
    }
  }

  async listBoards(): Promise<BoardRecord[]> {
    const boards = await this.runWithFallback(
      () => this.withStore<BoardRecord[]>(BOARDS_STORE, "readonly", (store) => store.getAll()),
      () => this.getFallbackBoards(),
    );
    return boards.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }

  async getBoard(id: string): Promise<BoardRecord | undefined> {
    return this.runWithFallback(
      () => this.withStore<BoardRecord | undefined>(BOARDS_STORE, "readonly", (store) => store.get(id)),
      () => this.getFallbackBoards().find((board) => board.id === id),
    );
  }

  async saveBoard(board: BoardRecord): Promise<void> {
    await this.runWithFallback(
      async () => {
        await this.withStore<IDBValidKey>(BOARDS_STORE, "readwrite", (store) => store.put(board));
      },
      () => {
        const boards = this.getFallbackBoards();
        const index = boards.findIndex((item) => item.id === board.id);
        if (index === -1) boards.push(board);
        else boards[index] = board;
        this.writeFallbackBoards(boards);
      },
    );
  }

  async deleteBoard(id: string): Promise<void> {
    await this.runWithFallback(
      async () => {
        await this.withStore<undefined>(BOARDS_STORE, "readwrite", (store) => store.delete(id));
      },
      () => this.writeFallbackBoards(this.getFallbackBoards().filter((board) => board.id !== id)),
    );
  }

  async getActiveBoardId(): Promise<string | undefined> {
    return this.runWithFallback(
      async () => {
        const meta = await this.withStore<StoredMeta | undefined>(META_STORE, "readonly", (store) =>
          store.get("activeBoardId"),
        );
        return meta?.value;
      },
      () => this.fallbackStorage?.getItem(FALLBACK_ACTIVE_KEY) ?? undefined,
    );
  }

  async setActiveBoardId(id: string): Promise<void> {
    await this.runWithFallback(
      async () => {
        await this.withStore<IDBValidKey>(META_STORE, "readwrite", (store) =>
          store.put({ key: "activeBoardId", value: id } satisfies StoredMeta),
        );
      },
      () => {
        if (!this.fallbackStorage) throw new StorageUnavailableError();
        this.fallbackStorage.setItem(FALLBACK_ACTIVE_KEY, id);
      },
    );
  }
}

export const boardStorage = new BoardStorage();
